import { useState, useMemo } from 'react';
import { Truck, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { format, parseISO, differenceInBusinessDays } from 'date-fns';

import { CopyableField } from '../components/CopyableField';
import { StatusBadge } from '../components/StatusBadge';
import { AttemptLog } from '../components/AttemptLog';
import { useProcurementItems, type ProcurementListItem } from '../hooks/useProcurement';
import { useProcurementAttempts, useCreateAttempt } from '../hooks/useProcurementAttempts';

function needsAction(item: ProcurementListItem): boolean {
  if (!item.lastAttemptAt) return true;
  return differenceInBusinessDays(new Date(), parseISO(item.lastAttemptAt)) > 3;
}

function ItemWithAttempts({ item }: { item: ProcurementListItem }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: attemptsData, isLoading: attemptsLoading } = useProcurementAttempts(
    isExpanded ? item.id : null
  );
  const createAttempt = useCreateAttempt();

  const attempts = attemptsData?.data ?? [];

  const isStale = needsAction(item);

  return (
    <div
      className={`px-4 py-3 border-b border-border last:border-b-0 ${
        isStale ? 'border-l-4 border-l-warning' : ''
      }`}
    >
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-0.5 rounded hover:bg-background-muted transition-colors"
            aria-label={isExpanded ? 'Collapse attempts' : 'Expand attempts'}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-foreground-muted" />
            ) : (
              <ChevronRight className="h-4 w-4 text-foreground-muted" />
            )}
          </button>

          <div className="flex items-center gap-4 flex-wrap min-w-0">
            <CopyableField value={item.partNumber} mono />
            <CopyableField value={item.quantity} />
            {item.vendorName && <CopyableField value={item.vendorName} />}
            {item.gpPoNumber && <CopyableField value={item.gpPoNumber} mono />}
          </div>
        </div>

        <div className="flex items-center gap-4 flex-shrink-0">
          {item.needByDate && (
            <span className="text-xs text-foreground-muted">
              Need by {format(parseISO(item.needByDate), 'MMM d')}
            </span>
          )}
          {item.expectedDockDate && (
            <span className="text-xs text-foreground-muted">
              ETA {format(parseISO(item.expectedDockDate), 'MMM d')}
            </span>
          )}
          <StatusBadge status={item.status} />
        </div>
      </div>

      {isExpanded && (
        <div className="mt-2 ml-7">
          <AttemptLog
            attempts={attempts}
            isLoading={attemptsLoading}
            onLogAttempt={(note) =>
              createAttempt.mutate({
                procurementItemId: item.id,
                attemptType: 'shipment',
                note,
              })
            }
            isSubmitting={createAttempt.isPending}
          />
        </div>
      )}
    </div>
  );
}

export function ShipmentPage() {
  const [needsActionOnly, setNeedsActionOnly] = useState(true);
  const { data, isLoading, isError } = useProcurementItems({ status: 'confirmed' });

  const items = data?.data ?? [];

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      // Sort by need-by date first (nulls last), then by priority (createdAt as proxy)
      if (a.needByDate && b.needByDate) {
        const cmp = a.needByDate.localeCompare(b.needByDate);
        if (cmp !== 0) return cmp;
      } else if (a.needByDate && !b.needByDate) {
        return -1;
      } else if (!a.needByDate && b.needByDate) {
        return 1;
      }
      return a.createdAt.localeCompare(b.createdAt);
    });
  }, [items]);

  const filteredItems = useMemo(() => {
    if (!needsActionOnly) return sortedItems;
    return sortedItems.filter(needsAction);
  }, [sortedItems, needsActionOnly]);

  const needsFollowUpCount = useMemo(
    () => items.filter(needsAction).length,
    [items]
  );

  const recentlyContactedCount = items.length - needsFollowUpCount;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-danger bg-danger-subtle p-4 text-sm text-danger">
        Failed to load procurement items. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          Awaiting Shipment
        </h1>
        <p className="text-sm text-foreground-muted mt-1">
          Follow up with vendors for shipping updates
        </p>
      </div>

      {/* Toggle + Summary Cards */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={needsActionOnly}
            onChange={(e) => setNeedsActionOnly(e.target.checked)}
            className="rounded border-border text-primary focus:ring-primary"
          />
          Needs action
        </label>

        <div className="flex items-center gap-4">
          <div className="rounded-lg border border-border bg-background px-4 py-2 text-center">
            <div className="text-lg font-semibold text-foreground">
              {items.length}
            </div>
            <div className="text-xs text-foreground-muted">Total awaiting</div>
          </div>
          <div className="rounded-lg border border-border bg-background px-4 py-2 text-center">
            <div className="text-lg font-semibold text-warning">
              {needsFollowUpCount}
            </div>
            <div className="text-xs text-foreground-muted">Needs follow-up</div>
          </div>
          <div className="rounded-lg border border-border bg-background px-4 py-2 text-center">
            <div className="text-lg font-semibold text-success">
              {recentlyContactedCount}
            </div>
            <div className="text-xs text-foreground-muted">
              Recently contacted
            </div>
          </div>
        </div>
      </div>

      {/* Items List */}
      {filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Truck className="h-12 w-12 text-foreground-subtle mb-4" />
          <h2 className="text-lg font-medium text-foreground mb-1">
            No orders awaiting shipment
          </h2>
          <p className="text-sm text-foreground-muted">
            {needsActionOnly && items.length > 0
              ? 'All items have been recently contacted. Turn off the filter to see all.'
              : 'Confirmed orders will appear here once vendors confirm.'}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-background overflow-hidden">
          {filteredItems.map((item) => (
            <ItemWithAttempts key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
